/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';

import DeliveryModel from 'cio/models/delivery';
import CampaignModel from 'cio/models/campaign';
import DeliveryDetailModel from 'cio/models/delivery-detail';
import NewsletterModel from 'cio/models/newsletter';
import TransactionalMessageModel from 'cio/models/transactional-message';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import CustomerModel from 'cio/models/customer';
import ActionModel from 'cio/models/action';
import EnvironmentService from 'cio/services/environment';
import SubjectModel from 'cio/models/subject';
import { isDeliveryMeasureEnabledForActionType } from 'cio/utils/metrics/measure';
import { Measure } from 'cio/utils/metrics/common';
import AlertsService from 'cio/services/alerts';
import AuthService from 'cio/services/auth';

interface CioDeliveryDetailArgs {
  delivery: DeliveryModel;
}

export default class CioDeliveryDetail extends Component<CioDeliveryDetailArgs> {
  @service environment!: EnvironmentService;
  @service alerts!: AlertsService;
  @service auth!: AuthService;

  @tracked campaign!: CampaignModel | null;
  @tracked newsletter: NewsletterModel | null = null;
  @tracked transactionalMessage: TransactionalMessageModel | null = null;
  @tracked broadcastTrigger: BroadcastTriggerModel | null = null;
  @tracked customer: CustomerModel | null = null;
  @tracked action: ActionModel | null = null;
  @tracked subject: SubjectModel | null = null;
  @tracked detail!: DeliveryDetailModel | null;

  get deliveryMetrics() {
    return this.args.delivery.actionInfo.deliveryMetrics?.filter((metric: Measure) =>
      isDeliveryMeasureEnabledForActionType(metric, this.args.delivery.actionType),
    );
  }

  get journeyLinkParams() {
    if (this.campaign?.isWebhookTriggered) {
      return {
        route: 'campaigns.campaign.overview.subjects.subject',
        models: [this.args.delivery.belongsTo('campaign').id(), this.args.delivery.belongsTo('journeySubject').id()],
      };
    }

    return {
      route: 'person.subjects.subject',
      models: [this.args.delivery.belongsTo('customer').id(), this.args.delivery.belongsTo('journeySubject').id()],
    };
  }

  maybeFetchRelationships = task({ drop: true }, async () => {
    let { delivery } = this.args;

    let parents = {
      campaign: await delivery.belongsTo('campaign').load(),
      broadcastTrigger: await delivery.belongsTo('broadcastTrigger').load(),
      newsletter: await delivery.belongsTo('newsletter').load(),
      transactionalMessage: await delivery.belongsTo('transactionalMessage').load(),
      customer: await delivery.belongsTo('customer').load(),
      action: await delivery.belongsTo('action').load(),
      subject: await delivery.belongsTo('journeySubject').load(),
    };

    this.campaign = parents.campaign;
    this.broadcastTrigger = parents.broadcastTrigger;
    this.newsletter = parents.newsletter;
    this.transactionalMessage = parents.transactionalMessage;
    this.customer = parents.customer;
    this.action = parents.action;
    this.subject = parents.subject;
  });

  constructor(owner: unknown, args: CioDeliveryDetailArgs) {
    super(owner, args);
    this.fetchDetailTask.perform();
  }

  get hasReadOnlyPermission() {
    if (this.newsletter != null) {
      return !this.auth.hasPermission('journeys/newsletters/edit');
    }

    if (this.transactionalMessage != null) {
      return !this.auth.hasPermission('journeys/transactional_messages/edit');
    }

    if (this.broadcastTrigger != null) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    if (this.campaign != null) {
      return !this.auth.hasPermission('journeys/campaigns/edit');
    }

    return;
  }

  fetchDetailTask = task({ drop: true }, async () => {
    try {
      this.detail = await this.args.delivery.belongsTo('detail').load();
    } catch {
      this.alerts.error('delivery.detail-error', "We weren't able to load the delivery detail");
    }
  });
}
